import { useEffect, useMemo } from 'react';
import { useRouter } from '../../hooks/useRouter';
import { useCopilotMaybe } from './CopilotContext';

const SIDEBAR_WIDTH_PX = 400;

/**
 * Easing + duration shared between the MUI Slide on the sidebar and the
 * push-content transition on the routes wrapper, so the two animations
 * stay in lockstep visually. Match these if you tune the Slide.
 */
export const COPILOT_PUSH_TRANSITION =
  'padding-right 225ms cubic-bezier(0.0, 0, 0.2, 1)';

/**
 * Page-aware sidebar behavior:
 *
 *  - On `/experiences/:id` (single Experience page), opening the copilot
 *    sidebar pushes the page content left by SIDEBAR_WIDTH_PX (HubSpot-style).
 *    On `/experiences` (listing), no push — the sidebar stays an overlay.
 *
 *  - Also on `/experiences/:id` only: when any modal dialog opens (MUI Dialog
 *    inserts a `[role="dialog"]` into the DOM), close the copilot sidebar so
 *    the modal isn't competing with it for attention.
 *
 * Designed to be called once per app — typically from <DadoCopilot>.
 */
export const useCopilotPageBehavior = () => {
  const ctx = useCopilotMaybe();
  // react-router-dom 5.0.0 predates hooks — useRouter() (__RouterContext)
  // provides a reactive location in place of useLocation.
  const { location } = useRouter();
  const pathname = location.pathname;

  // /experiences/:id and any sub-routes — but NOT the bare /experiences listing
  const isOnSingleExperiencePage = useMemo(
    () => /^\/experiences\/[^/]+/.test(pathname),
    [pathname]
  );

  const shouldPush = !!ctx?.isOpen && isOnSingleExperiencePage;

  // Push behavior: expose the push width as a CSS custom property on <body>.
  // Consumers (the routes wrapper in App.tsx) read it via var(--copilot-push)
  // and apply their own padding-right + transition. Body itself stays
  // un-padded so the top nav (sticky inside body) is unaffected.
  useEffect(() => {
    document.body.style.setProperty(
      '--copilot-push',
      shouldPush ? `${SIDEBAR_WIDTH_PX}px` : '0px'
    );
  }, [shouldPush]);

  // Final cleanup on unmount — clear the variable in case the provider
  // unmounts mid-animation.
  useEffect(() => {
    return () => {
      document.body.style.removeProperty('--copilot-push');
    };
  }, []);

  // Auto-close on modal open. Watches the entire body subtree for nodes that
  // are or contain a `[role="dialog"]` — MUI's Dialog renders one. Popovers,
  // menus, and tooltips don't get role="dialog", so they don't trigger this.
  useEffect(() => {
    if (!ctx?.isOpen || !isOnSingleExperiencePage) return;

    const containsDialog = (node: Node): boolean => {
      if (!(node instanceof HTMLElement)) return false;
      if (node.matches('[role="dialog"]')) return true;
      return node.querySelector('[role="dialog"]') !== null;
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const added of mutation.addedNodes) {
          if (containsDialog(added)) {
            ctx.close();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ctx, ctx?.isOpen, isOnSingleExperiencePage]);
};
